import React, {useEffect, useRef, useState} from 'react';
import {useThemeProvider} from '../utils/ThemeContext';
import {chartColors,getHtmlLegendPlugin} from './ChartjsConfig';
import {ArcElement, Chart, DoughnutController, TimeScale, Tooltip,} from 'chart.js';
import 'chartjs-adapter-moment';

// Import utilities
import {tailwindConfig} from '../utils/Utils';

Chart.register(DoughnutController, ArcElement, TimeScale, Tooltip);

function DoughnutChart({
                           data,
                           width,
                           height
                       }) {

    const [chart, setChart] = useState(null);
    const canvas = useRef(null);
    const legend = useRef(null);
    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {tooltipTitleColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    useEffect(() => {
        const ctx = canvas.current;
        // eslint-disable-next-line no-unused-vars
        const newChart = new Chart(ctx, {
            type: 'doughnut',
            data: data,
            options: {
                cutout: '80%',
                layout: {
                    padding: 24,
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        titleColor: darkMode ? tooltipTitleColor.dark : tooltipTitleColor.light,
                        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                        backgroundColor: darkMode ? tooltipBgColor.dark : tooltipBgColor.light,
                        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                    },
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest',
                },
                animation: {
                    duration: 500,
                },
                maintainAspectRatio: false,
                resizeDelay: 200,
            },
            plugins: [
                {
                    id: 'htmlLegend',
                    afterUpdate(c, args, options) {
                        const ul = legend.current;
                        if (!ul) return;
                        // Remove old legend items
                        while (ul.firstChild) {
                            ul.firstChild.remove();
                        }
                        // Reuse the built-in legendItems generator
                        const items = c.options.plugins.legend.labels.generateLabels(c);
                        items.forEach((item) => {
                            const li = document.createElement('li');
                            li.style.margin = tailwindConfig().theme.margin[1];
                            // Button element
                            const button = document.createElement('button');
                            button.className = 'btn-xs bg-white text-xs dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full ' +
                                'border-gray-200 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-400';
                            button.style.opacity = item.hidden ? '.3' : '';
                            button.onclick = () => {
                                c.toggleDataVisibility(item.index);
                                c.update();
                            };
                            // Color box
                            const box = document.createElement('span');
                            box.style.display = 'block';
                            box.style.width = tailwindConfig().theme.width[2];
                            box.style.height = tailwindConfig().theme.height[2];
                            box.style.backgroundColor = item.fillStyle;
                            box.style.borderRadius = tailwindConfig().theme.borderRadius.sm;
                            box.style.marginRight = tailwindConfig().theme.margin[1];
                            box.style.pointerEvents = 'none';
                            // Label
                            const label = document.createElement('span');
                            label.style.display = 'flex';
                            label.style.alignItems = 'center';
                            const labelText = document.createTextNode(item.text);
                            label.appendChild(labelText);
                            li.appendChild(button);
                            button.appendChild(box);
                            button.appendChild(label);
                            ul.appendChild(li);
                        });
                    },
                },
            ],
        });
        setChart(newChart);
        return () => newChart.destroy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!chart) return;

        if (darkMode) {
            chart.options.plugins.tooltip.titleColor = tooltipTitleColor.dark;
            chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
            chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
            chart.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
        } else {
            chart.options.plugins.tooltip.titleColor = tooltipTitleColor.light;
            chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
            chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
            chart.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTheme, data]);

    return (
        <div className="chart--doughnut grow flex flex-col justify-start">
            <div>
                <canvas ref={canvas} width={width} height={height}></canvas>
            </div>
            <div className="px-5 pt-2 pb-6">
                <ul ref={legend} className="flex flex-wrap justify-center -m-1"></ul>
            </div>
        </div>
    );
}

export default DoughnutChart;